import { useCallback, useEffect, useState } from 'react';

import { Card } from '~/eds/src/components/Card/Card';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { AlgorithmStatusType } from '~/enums/AlgorithmStatusType';
import { isWssField as checkIsWssField } from '~/features/document-handler/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

import { EditCommandPanel } from './components/EditCommandPanel';
import { FieldRenderer } from './components/FieldRenderer';
import { useDocumentEdit } from './hooks/useDocumentEdit';
import {
  DocumentOverviewPanelProps,
  OverviewField,
  OverviewSection,
} from './types';

function DocumentOverviewPanel({
  documentId,
  dateModified,
  sections,
  onEditSubmitted,
  userVisibilityLevel,
  isTerminated = false,
  OCRProcessing,
}: DocumentOverviewPanelProps) {
  const [showAllParties, setShowAllParties] = useState(false);

  const isWssDesignatedWorkflowEnabled = useFlag(
    FlagType.WSSDesignatedWorkflow,
  );
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const isDownloadDocumentAttachmentEnabled = useFlag(
    FlagType.DownloadDocumentAttachment,
  );
  const isFieldValueMigrationDeferredFeaturesBypassEnabled = useFlag(
    FlagType.FieldValueMigrationDeferredFeaturesBypass,
  );

  const {
    editMode,
    setEditMode,
    editData,
    isSaving,
    initializeEditData,
    handleDiscardChanges,
    handleSaveChanges,
    handleEditDataChange,
    handleDateChange,
    handleFileAttachError,
  } = useDocumentEdit({
    documentId,
    sections,
    onEditSubmitted,
  });

  useEffect(() => {
    initializeEditData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  const isWssField = useCallback(
    (field: OverviewField) => {
      if (isWSSIntegrationPhase2M1M3Enabled) {
        return false;
      }
      return isWssDesignatedWorkflowEnabled && checkIsWssField(field as any);
    },
    [isWssDesignatedWorkflowEnabled, isWSSIntegrationPhase2M1M3Enabled],
  );

  const handleOnPartiesShowAllClick = useCallback(() => {
    setShowAllParties(true);
  }, []);

  const renderSection = (section: OverviewSection) => {
    return (
      <Layout wrap align="flex-end" flex="1">
        {section.fields.map((field) => (
          <FieldRenderer
            key={`field-${field.id}`}
            field={field}
            editMode={editMode}
            editData={editData}
            documentId={documentId}
            showAllParties={showAllParties}
            isDownloadDocumentAttachmentEnabled={
              isDownloadDocumentAttachmentEnabled
            }
            onEditDataChange={handleEditDataChange}
            onDateChange={handleDateChange}
            onFileAttachError={handleFileAttachError}
            onShowAllParties={handleOnPartiesShowAllClick}
            isWssField={isWssField}
          />
        ))}
      </Layout>
    );
  };

  const renderInformationSections = (sections: OverviewSection[]) => {
    return sections.map((section) => (
      <Card
        key={section.title}
        mode="bordered"
        header={{
          title: section.title,
        }}
      >
        {renderSection(section)}
      </Card>
    ));
  };

  const bypassEditInOverview =
    isFieldValueMigrationDeferredFeaturesBypassEnabled;
  const canEdit =
    userVisibilityLevel === 'OPEN' && !bypassEditInOverview && !isTerminated;

  if (OCRProcessing?.status === AlgorithmStatusType.Failed) {
    return sections.length ? (
      <Layout direction="column" spacing={6}>
        {renderInformationSections(sections)}
      </Layout>
    ) : null;
  }

  return (
    <Layout direction="column" spacing={6}>
      <EditCommandPanel
        editMode={editMode}
        isSaving={isSaving}
        canEdit={canEdit}
        documentId={documentId}
        dateModified={dateModified}
        OCRProcessing={OCRProcessing}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveChanges}
        onEditClick={() => setEditMode(true)}
      />
      {sections.length ? renderInformationSections(sections) : null}
    </Layout>
  );
}

export default DocumentOverviewPanel;
